// @flow

import classNames from 'classnames';
import gql from 'graphql-tag';
import React, {Fragment} from 'react';
import {FormattedMessage} from 'react-intl';
import {connect} from 'react-redux';
import SegmentationExperiment from '../../react-segmentation/components/Experiment/Experiment';
import checkFamilyContext from '../../react-segmentation/utils/checkFamilyContext';
import {HA_TRAVSEG_SERP_AMENITIES} from '../../abacus/experiments';

import type {State as ReduxState} from '../../reducers/index';

import './Details.less';

export const fragments = {
    SERPHit: gql`
        fragment DetailsSERPHit on SERPHit {
            details {
                sleeps
                bedrooms
                bathroomsFull
                bathroomsHalf
                bathroomsToiletOnly
                area
            }
            amenitiesBadges {
                name
            }
        }
    `
};

type StateProps = {
    isFamilyContext?: boolean
};

export type Shape = {
    sleeps: number,
    bedrooms: number,
    bathroomsFull: number,
    bathroomsHalf: number,
    bathroomsToiletOnly: number,
    area: number
};

export type Props = {
    amenitiesBadges?: {name: string}[],
    details: Shape,
    isMetric: boolean,
    propertyType: string,
    shouldHideArea?: boolean,
    shouldShowFamilyAmenities?: boolean
} & StateProps;

export type AreaElementType = {
    area: number,
    isMetric?: boolean
};

export type BedroomsPropertyTypeElementType = {
    bedrooms: number,
    propertyType: string
};

export type DetailsItemShape = {
    id: string,
    classname: string,
    value: number
};

export type HalfBathElementType = {
    bathroomsHalf: number,
    bathroomsToiletOnly: number
};

export type BathroomsElementType = {
    bathroomsFull: number,
    bathroomsHalf: number,
    bathroomsToiletOnly: number
};

export type SleepsElementShape = {
    value: number
};

export function DetailsItem({id, classname, value}: DetailsItemShape) {
    if (value && value > 0) {
        return (
            <div
                className={classNames(
                    `Details__${classname}`,
                    'text-capitalize',
                    'Details__label'
                )}
            >
                <FormattedMessage
                    id={id}
                    description={`${classname} label`}
                    defaultMessage={`${classname}`}
                >
                    {(txt) => (
                        <Fragment>
                            {value} {txt}
                        </Fragment>
                    )}
                </FormattedMessage>
            </div>
        );
    }
    return null;
}

export function BedroomsPropertyTypeElement({
    bedrooms,
    propertyType
}: BedroomsPropertyTypeElementType) {
    if (bedrooms > 0) {
        return (
            <Fragment>
                <div className="Details__propertyType Details__label">
                    {propertyType}
                </div>
                <div className="Details__bedrooms Details__label">
                    <FormattedMessage
                        id="bedroomsAbbreviationParam"
                        description="bedrooms abbreviation label"
                        defaultMessage="BR"
                        values={{
                            bedrooms
                        }}
                    >
                        {(txt) => <Fragment>{txt}</Fragment>}
                    </FormattedMessage>
                </div>
            </Fragment>
        );
    }
    return (
        <div className="Details__propertyType Details__item">
            {propertyType}
        </div>
    );
}

export function AreaElement({area, isMetric}: AreaElementType) {
    if (area && area > 0) {
        return (
            <div className="Details__area Details__item Details__label">
                <span className="Details__value">{area}</span>
                <span
                    className={classNames({
                        'text-capitalize': !isMetric
                    })}
                >
                    <FormattedMessage
                        id="areaLabel"
                        description="area label"
                        defaultMessage="Sq. Ft"
                        values={{METRIC: isMetric}}
                    >
                        {(txt) => <Fragment>{txt}</Fragment>}
                    </FormattedMessage>
                </span>
            </div>
        );
    }
    return null;
}

export function HalfBathElement({
    bathroomsHalf,
    bathroomsToiletOnly
}: HalfBathElementType) {
    if (bathroomsHalf > 0) {
        return (
            <DetailsItem
                id="bathroomsHalfAbbreviation"
                classname="halfbathrooms"
                value={bathroomsHalf}
            />
        );
    } else if (bathroomsToiletOnly > 0) {
        return (
            <DetailsItem
                id="bathroomsToiletOnlyAbbreviation"
                classname="halfbathrooms"
                value={bathroomsToiletOnly}
            />
        );
    }
    return null;
}

export function SleepsElement({value}: SleepsElementShape) {
    if (value && value > 0) {
        return (
            <div className="Details__sleeps Details__label">
                <FormattedMessage
                    id="sleepsAbbreviation"
                    description="sleeps label"
                    defaultMessage="Sleeps {value}"
                    values={{
                        value
                    }}
                >
                    {(txt) => <Fragment>{txt}</Fragment>}
                </FormattedMessage>
            </div>
        );
    }
    return null;
}

export function BathroomsElement({
    bathroomsFull,
    bathroomsHalf,
    bathroomsToiletOnly
}: BathroomsElementType) {
    if (bathroomsFull > 0) {
        return (
            <Fragment>
                <div className="Details__bathrooms Details__label">
                    <FormattedMessage
                        id="bathroomsFullAbbreviationParam"
                        description={`bathrooms label`}
                        defaultMessage={`bathrooms`}
                        values={{
                            bathroomsFull
                        }}
                    >
                        {(txt) => <Fragment>{txt}</Fragment>}
                    </FormattedMessage>
                </div>
                <HalfBathElement
                    bathroomsHalf={bathroomsHalf}
                    bathroomsToiletOnly={bathroomsToiletOnly}
                />
            </Fragment>
        );
    }
    return null;
}

export function Details({
    amenitiesBadges,
    details: {
        sleeps,
        bedrooms,
        bathroomsFull,
        bathroomsHalf,
        bathroomsToiletOnly,
        area
    },
    isFamilyContext,
    isMetric,
    propertyType
}: Props) {
    return (
        <Fragment>
            <BedroomsPropertyTypeElement
                bedrooms={bedrooms}
                propertyType={propertyType}
            />
            <BathroomsElement
                bathroomsFull={bathroomsFull}
                bathroomsHalf={bathroomsHalf}
                bathroomsToiletOnly={bathroomsToiletOnly}
            />
            <SleepsElement value={sleeps} />
            <SegmentationExperiment experimentName={HA_TRAVSEG_SERP_AMENITIES}>
                {({evaluationData: {value: evaluationDataValue}}) => {
                    const shouldShowArea =
                        !(isFamilyContext && evaluationDataValue === 2) ||
                        !(amenitiesBadges && amenitiesBadges.length);
                    const shouldShowAmenities = !!(
                        isFamilyContext && evaluationDataValue
                    );
                    return (
                        <Fragment>
                            {shouldShowArea && (
                                <AreaElement area={area} isMetric={isMetric} />
                            )}
                            {shouldShowAmenities &&
                                !!(amenitiesBadges && amenitiesBadges.length) &&
                                amenitiesBadges.map(({name}) => (
                                    <div className="Details__label" key={name}>
                                        {name}
                                    </div>
                                ))}
                        </Fragment>
                    );
                }}
            </SegmentationExperiment>
        </Fragment>
    );
}

const mapStateToProps = ({searchParams}: ReduxState) => ({
    isFamilyContext: checkFamilyContext(searchParams)
});

export default connect(mapStateToProps)(Details);
